import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import isMobile from 'helpers/isMobile';
import { renderMarkdown, resetMarkdownCache } from 'helpers/markdown';
import { trackOptimizelyEvent } from 'helpers/optimizely';
import { isEmpty, isPresent } from 'helpers/verify';
import { CalloutCard } from 'components/CardSelection';
import { LocationSelector } from 'components/CmsPage/modules/LocationSelector/LocationSelector';
import { PLAN } from 'constants/plan-types';
import { readSessionStorage } from 'redux/saga/helpers/local-storage';

// The Nike-specific methods are a temporary hack to enable
// Nike Plus Unlock card decorations in full-fare pricing pages, before
// we rework pricing pages to handle contextual promotions.
const NIKEFIVE = 'nikefive';
const NIKETEN = 'niketen';

export class PlanSelection extends Component {
  constructor(props) {
    super(props);
    this.handleSelectPlanClick = this.handleSelectPlanClick.bind(this);
    this.state = {
      selectedItemId: null,
      selectedItemType: null,
    };
  }

  componentDidMount() {
    this.resetCardSelectionState(this.props.cards);
  }

  componentDidUpdate(prevProps) {
    const { cards, loadCardData, plansModuleConfig } = this.props;

    if (plansModuleConfig !== prevProps.plansModuleConfig) {
      loadCardData(plansModuleConfig);
    }

    if (isMobile() && !this.areCardsEqual(cards, prevProps.cards)) {
      this.resetCardSelectionState(cards);
    }
  }

  onDwell() {
    trackOptimizelyEvent('view_plans');
  }

  getCpOptimize() {
    try {
      return this.props.isClient ? readSessionStorage('cpOptimize') : null;
    } catch (e) {
      return null;
    }
  }

  areCardsEqual(cards, prevCards) {
    return cards.length === prevCards.length && cards.every((card, idx) => card.itemId === prevCards[idx].itemId);
  }

  decorateNikePlusCards(cards) {
    const decorateIndex = {
      [NIKEFIVE]: 1,
      [NIKETEN]: 2,
    }[this.getCpOptimize()];

    const targetCard = cards[decorateIndex];

    if (!targetCard || targetCard.isNikePlusDecorated) {
      return cards;
    }

    const planPrice = targetCard.callout.replace(/\*/g, '');
    cards[decorateIndex] = {
      ...targetCard,
      callout: '$*0*',
      calloutNote: `~~${planPrice}~~ first month`,
      isNikePlusDecorated: true,
      tag: 'NikePlus Unlock',
    };

    // When we SSR, we don't have sessionStorage available.
    // So the first render renders without the NikePlus decoration,
    // and the client-side render would otherwise be cached.
    // This only executes once, since we don't re-decorate cards.
    resetMarkdownCache();

    return cards;
  }

  trackNikePlus() {
    const classCount = {
      [NIKEFIVE]: 5,
      [NIKETEN]: 10,
    }[this.getCpOptimize()];

    trackOptimizelyEvent(`NikePlus Unlock ${classCount}-Class`);
  }

  isNikePlusUnlock() {
    const cards = this.props.cards || [];
    return (
      cards.length === 3 &&
      cards.every(card => card.type === PLAN) &&
      [NIKEFIVE, NIKETEN].includes(this.getCpOptimize())
    );
  }

  resetCardSelectionState(cards) {
    this.setState({
      selectedItemId: !isEmpty(cards) ? cards[0].itemId : 0,
      selectedItemType: !isEmpty(cards) ? cards[0].type : 0,
    });
  }

  handleSelectPlanClick() {
    const { selectedItemId, selectedItemType } = this.state;
    this.props.selectPlan(selectedItemType, selectedItemId);
  }

  handleCardClick(itemType, itemId, isNikePlus) {
    if (isMobile()) {
      this.setState({
        selectedItemId: itemId,
        selectedItemType: itemType,
      });
    } else {
      this.props.selectPlan(itemType, itemId);
    }

    if (isNikePlus) {
      this.trackNikePlus();
    }
  }

  renderCards() {
    const cards = this.props.cards || [];

    return (this.isNikePlusUnlock() ? this.decorateNikePlusCards(cards) : cards).map(card => (
      <CalloutCard
        callout={renderMarkdown(card.callout, `card_callout_${card.type}_${card.itemId}`)}
        calloutNote={renderMarkdown(card.calloutNote, `card_calloutNote_${card.type}_${card.itemId}`)}
        ctaCopy={card.cta}
        details={renderMarkdown(card.details, `card_details_${card.type}_${card.itemId}`)}
        key={card.itemId}
        onClick={() => this.handleCardClick(card.type, card.itemId, card.isNikePlusDecorated)}
        selected={card.itemId === this.state.selectedItemId}
        tag={renderMarkdown(card.tag, `card_tag_${card.type}_${card.itemId}`)}
      />
    ));
  }

  renderCta() {
    return (
      <button
        className="bt bt--primary bt--lg plan-selection__cta"
        data-qa="PlanSelection.cta"
        onClick={this.handleSelectPlanClick}>
        <FormattedMessage id="plan-selection.card.cta.select-plan" defaultMessage="Select plan" />
      </button>
    );
  }

  render() {
    const { cards, creditsPerClassAverage, disclaimer, msaSelectorLeadin, subtitle, subtitleStyle, title } = this.props;

    if (isEmpty(cards)) {
      return false;
    }

    return (
      <section>
        <div className="pricing color-background-light">
          <div
            className={classNames('pricing__subtitle', `pricing__subtitle--${subtitleStyle}`, {
              'hidden md-block': subtitleStyle === 'subtle',
            })}>
            {subtitle}
          </div>
          <h2 className="pricing__header">{title}</h2>
          <div className="pricing__location-input pb++">
            <div className="pricing__location-input__location">
              <LocationSelector label={msaSelectorLeadin} />
            </div>
          </div>
        </div>
        <div className="pricing__plans plan-selection">
          {this.renderCards()}
          {this.renderCta()}
        </div>
        {disclaimer && <p className="pricing__disclaimer text--center">{disclaimer}</p>}
        {!disclaimer && isPresent(creditsPerClassAverage) && (
          <p className="pricing__disclaimer text--center">
            <FormattedMessage
              id="plan-selection.disclaimer.average-credits-per-class"
              defaultMessage="The average class in your area costs {creditsPerClassAverage, plural, one {# credit} other {# credits}}."
              values={{ creditsPerClassAverage }}
            />
          </p>
        )}
      </section>
    );
  }
}

PlanSelection.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      callout: PropTypes.string.isRequired,
      calloutNote: PropTypes.string.isRequired,
      cta: PropTypes.string.isRequired,
      details: PropTypes.string,
      itemId: PropTypes.number,
      tag: PropTypes.string,
      type: PropTypes.oneOf(['plan', 'offer']),
    })
  ),
  creditsPerClassAverage: PropTypes.number,
  disclaimer: PropTypes.string,
  isClient: PropTypes.bool.isRequired,
  msaSelectorLeadin: PropTypes.string,
  plansModuleConfig: PropTypes.object,
  selectPlan: PropTypes.func.isRequired,
  loadCardData: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  subtitleStyle: PropTypes.oneOf(['subtle', 'loud']),
  title: PropTypes.string.isRequired,
};
